import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../../../shared/lib/supabase'
import { usePollingInterval } from '../../../hooks/usePollingInterval'

export function useGlobalActivity({ orgId, action, limit = 50, offset = 0 } = {}) {
  const [activities, setActivities] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastRefreshed, setLastRefreshed] = useState(null)
  const isFirstLoad = useRef(true)

  const fetchActivities = useCallback(async () => {
    // Only show full loading spinner on first load, not on poll refreshes
    if (isFirstLoad.current) setLoading(true)
    setError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        setError('Not authenticated')
        setLoading(false)
        return
      }

      const params = new URLSearchParams()
      if (orgId) params.set('org_id', orgId)
      if (action) params.set('action', action)
      params.set('limit', String(limit))
      params.set('offset', String(offset))
      params.set('since', new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString())

      const res = await fetch(`/api/global/activity?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}))
        throw new Error(errData.error || 'Failed to fetch activity')
      }

      const data = await res.json()
      setActivities(data.activities || [])
      setTotalCount(data.total_count || 0)
      setLastRefreshed(new Date())
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
      isFirstLoad.current = false
    }
  }, [orgId, action, limit, offset])

  useEffect(() => {
    isFirstLoad.current = true
    fetchActivities()
  }, [fetchActivities])

  // 5-minute polling
  usePollingInterval(fetchActivities, 5 * 60 * 1000, true)

  return { activities, totalCount, loading, error, refresh: fetchActivities, lastRefreshed }
}
